import { Globe2 } from 'lucide-react';
import { useLocationContext } from '@/contexts/LocationContext';
import { cn } from '@/lib/utils';

/**
 * The set of options surfaced in V2's top toggle. Each option points at an
 * existing `selectedLocation` value tracked by LocationContext (so the cookie
 * round-trips automatically with classic mode).
 */
const OPTIONS: Array<{ id: string; label: string; location: string; tone: string }> = [
  { id: 'all', label: 'ALL', location: 'All, USA', tone: 'text-slate-500' },
  { id: 'wa', label: 'WA', location: 'Auburn, WA', tone: 'text-emerald-600' },
  { id: 'tx', label: 'TX', location: 'Grand Prairie, TX', tone: 'text-red-600' },
];

export function LocationStateToggle() {
  const { selectedLocation, setSelectedLocation } = useLocationContext();
  const activeId =
    OPTIONS.find((o) => o.location === selectedLocation)?.id ?? 'all';

  return (
    <div className="inline-flex items-center border border-slate-200 bg-white rounded-md p-0.5 h-8 text-xs">
      {OPTIONS.map((opt) => {
        const active = opt.id === activeId;
        return (
          <button
            key={opt.id}
            onClick={() => setSelectedLocation(opt.location)}
            className={cn(
              'inline-flex items-center gap-1.5 h-7 px-2.5 rounded-sm font-semibold tracking-wider transition-colors',
              active
                ? 'bg-slate-900 text-white'
                : 'text-slate-500 hover:text-slate-900 hover:bg-slate-50'
            )}
            title={`Filter to ${opt.label}`}
          >
            <StateIcon
              id={opt.id}
              className={cn('w-3.5 h-3.5', active ? 'text-white' : opt.tone)}
            />
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

interface StateIconProps {
  id: string;
  className?: string;
}

function StateIcon({ id, className }: StateIconProps) {
  if (id === 'all') {
    return <Globe2 className={className} />;
  }
  if (id === 'wa') {
    return (
      <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden>
        {/* Washington: roughly rectangular, notched on the NW (Puget Sound). */}
        <path d="M3 7 L9 7 L11 5 L21 5 L21 17 L3 17 L4 12 Z" />
      </svg>
    );
  }
  if (id === 'tx') {
    return (
      <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden>
        {/* Texas: panhandle on top-left, gulf curve on bottom-right. */}
        <path d="M4 2 L8 2 L10 4 L14 4 L18 6 L22 8 L22 12 L20 16 L16 20 L12 20 L8 16 L4 10 L4 6 Z" />
      </svg>
    );
  }
  return null;
}
